import type { ResearchSource } from "../types/api";

export interface ResearchAnalyticsFilter {
  year: number | null;
  venue: string | null;
}

export interface ResearchCount<T> {
  key: T;
  count: number;
}

export interface ResearchSourceAnalytics {
  total: number;
  withYear: number;
  withVenue: number;
  venueTotal: number;
  firstYear: number | null;
  lastYear: number | null;
  yearCounts: Array<ResearchCount<number>>;
  venueCounts: Array<ResearchCount<string>>;
  publicationTypeCounts: Array<ResearchCount<NonNullable<ResearchSource["publicationType"]>>>;
}

function countBy<T>(values: T[]): Array<ResearchCount<T>> {
  const counts = new Map<T, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);
  return [...counts].map(([key, count]) => ({ key, count }));
}

export function summarizeResearchSources(sources: ResearchSource[]): ResearchSourceAnalytics {
  const papers = sources.filter((source) => source.sourceType === "paper");
  const years = papers.map((source) => source.year).filter((year): year is number => Number.isInteger(year));
  const venues = papers.map((source) => source.venue?.trim()).filter((venue): venue is string => Boolean(venue));
  const publicationTypes = papers.map((source) => source.publicationType).filter((type): type is NonNullable<ResearchSource["publicationType"]> => Boolean(type));
  const sortedYears = [...years].sort((left, right) => left - right);
  return {
    total: papers.length,
    withYear: years.length,
    withVenue: venues.length,
    venueTotal: new Set(venues).size,
    firstYear: sortedYears[0] ?? null,
    lastYear: sortedYears.at(-1) ?? null,
    yearCounts: countBy(years).sort((left, right) => left.key - right.key),
    venueCounts: countBy(venues).sort((left, right) => right.count - left.count || left.key.localeCompare(right.key)).slice(0, 12),
    publicationTypeCounts: countBy(publicationTypes).sort((left, right) => right.count - left.count)
  };
}

export function filterResearchSources(sources: ResearchSource[], filter: ResearchAnalyticsFilter): ResearchSource[] {
  return sources.filter((source) => (filter.year == null || source.year === filter.year) && (filter.venue == null || source.venue === filter.venue));
}
